'use client';
import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import {
  Flame,
  Plus,
  Check,
  Pencil,
  Trash2,
  Loader2,
  FileText,
  Calendar,
  Building2,
  Hash,
  X,
} from 'lucide-react';
import { useClaimContext } from '@/contexts/ClaimContext';
import { deleteClaim } from '@/lib/claimService';
import ConfirmModal from '@/components/ui/ConfirmModal';

interface NewClaimForm {
  name: string;
}

export default function ClaimsManagerContent() {
  const router = useRouter();
  const { claims, activeClaim, loadingClaims, switchClaim, addClaim, renameClaim, refreshClaims } =
    useClaimContext();

  const [showNewForm, setShowNewForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [switching, setSwitching] = useState<string | null>(null);

  const handleCreate = async () => {
    const name = newName.trim() || `Claim ${claims.length + 1}`;
    setCreating(true);
    const claim = await addClaim(name);
    setCreating(false);
    setNewName('');
    setShowNewForm(false);
    if (claim) {
      await switchClaim(claim.id);
      router.push('/');
    }
  };

  const handleSwitch = async (id: string) => {
    if (id === activeClaim?.id) {
      router.push('/');
      return;
    }
    setSwitching(id);
    await switchClaim(id);
    setSwitching(null);
    router.push('/');
  };

  const startEdit = (id: string, currentName: string) => {
    setEditingId(id);
    setEditName(currentName);
  };

  const saveEdit = async () => {
    if (!editingId || !editName.trim()) return;
    setSavingEdit(true);
    await renameClaim(editingId, editName.trim());
    setSavingEdit(false);
    setEditingId(null);
    setEditName('');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName('');
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    await deleteClaim(deleteTarget.id);
    await refreshClaims();
    setDeleting(false);
    setDeleteTarget(null);
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return null;
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return null;
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Claims</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Create and switch between multiple insurance claims. Each claim has its own inventory,
            settings, and data.
          </p>
        </div>
        <button
          onClick={() => {
            setShowNewForm(true);
            setNewName('');
          }}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-semibold hover:bg-amber-700 transition-colors shrink-0"
        >
          <Plus size={15} />
          New Claim
        </button>
      </div>

      {/* New claim form */}
      {showNewForm && (
        <div className="bg-card border border-primary/30 rounded-xl p-4 shadow-sm space-y-3">
          <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Flame size={14} className="text-primary" />
            Create New Claim
          </h2>
          <div className="flex gap-2">
            <input
              autoFocus
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreate();
                if (e.key === 'Escape') setShowNewForm(false);
              }}
              placeholder={`e.g. Smith House Fire – ${new Date().getFullYear()}`}
              className="flex-1 px-3 py-2 text-sm border border-border rounded-lg bg-input focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <button
              onClick={handleCreate}
              disabled={creating}
              className="flex items-center gap-1.5 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-semibold hover:bg-amber-700 disabled:opacity-50 transition-colors"
            >
              {creating ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              Create
            </button>
            <button
              onClick={() => setShowNewForm(false)}
              className="p-2 rounded-lg hover:bg-secondary transition-colors text-muted-foreground"
              aria-label="Cancel"
            >
              <X size={16} />
            </button>
          </div>
          <p className="text-xs text-muted-foreground">
            After creating, you'll be switched to the new claim automatically.
          </p>
        </div>
      )}

      {/* Claims list */}
      {loadingClaims ? (
        <div className="flex items-center gap-2 py-12 justify-center text-muted-foreground text-sm">
          <Loader2 size={16} className="animate-spin" />
          Loading claims…
        </div>
      ) : claims.length === 0 ? (
        <div className="text-center py-16 bg-card border border-border rounded-xl">
          <Flame size={32} className="mx-auto text-muted-foreground mb-3 opacity-40" />
          <p className="text-sm font-medium text-foreground">No claims yet</p>
          <p className="text-xs text-muted-foreground mt-1">
            Create your first claim to start tracking inventory.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {claims.map((claim) => {
            const isActive = claim.id === activeClaim?.id;
            const isEditing = editingId === claim.id;
            const isSwitching = switching === claim.id;

            return (
              <div
                key={claim.id}
                className={[
                  'bg-card border rounded-xl p-4 transition-all',
                  isActive
                    ? 'border-primary/50 shadow-sm ring-1 ring-primary/20'
                    : 'border-border hover:border-border/80',
                ].join(' ')}
              >
                <div className="flex items-start gap-3">
                  {/* Active indicator */}
                  <div
                    className={[
                      'mt-0.5 w-8 h-8 rounded-lg flex items-center justify-center shrink-0',
                      isActive ? 'bg-primary/10' : 'bg-secondary',
                    ].join(' ')}
                  >
                    <Flame
                      size={16}
                      className={isActive ? 'text-primary' : 'text-muted-foreground'}
                    />
                  </div>

                  {/* Claim info */}
                  <div className="flex-1 min-w-0">
                    {isEditing ? (
                      <div className="flex gap-2 mb-2">
                        <input
                          autoFocus
                          type="text"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveEdit();
                            if (e.key === 'Escape') cancelEdit();
                          }}
                          className="flex-1 px-2 py-1 text-sm border border-border rounded-lg bg-input focus:outline-none focus:ring-2 focus:ring-ring"
                        />
                        <button
                          onClick={saveEdit}
                          disabled={savingEdit}
                          className="flex items-center gap-1 px-3 py-1 bg-primary text-primary-foreground rounded-lg text-xs font-medium hover:bg-amber-700 disabled:opacity-50 transition-colors"
                        >
                          {savingEdit ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                          Save
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="p-1.5 rounded-lg hover:bg-secondary transition-colors text-muted-foreground"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-sm text-foreground truncate">
                          {claim.name}
                        </span>
                        {isActive && (
                          <span className="px-1.5 py-0.5 text-xs font-medium bg-primary/10 text-primary rounded-full shrink-0">
                            Active
                          </span>
                        )}
                      </div>
                    )}

                    {/* Meta details */}
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      {claim.claimNumber && (
                        <span className="flex items-center gap-1">
                          <Hash size={11} />
                          {claim.claimNumber}
                        </span>
                      )}
                      {claim.insuranceCompany && (
                        <span className="flex items-center gap-1">
                          <Building2 size={11} />
                          {claim.insuranceCompany}
                        </span>
                      )}
                      {claim.dateOfLoss && (
                        <span className="flex items-center gap-1">
                          <Calendar size={11} />
                          Loss: {formatDate(claim.dateOfLoss)}
                        </span>
                      )}
                      {claim.policyNumber && (
                        <span className="flex items-center gap-1">
                          <FileText size={11} />
                          Policy: {claim.policyNumber}
                        </span>
                      )}
                      {!claim.claimNumber && !claim.insuranceCompany && !claim.dateOfLoss && !claim.policyNumber && (
                        <span className="italic">No details added yet</span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 shrink-0">
                    {!isEditing && (
                      <>
                        <button
                          onClick={() => startEdit(claim.id, claim.name)}
                          className="p-1.5 rounded-lg hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground"
                          title="Rename claim"
                        >
                          <Pencil size={14} />
                        </button>
                        {claims.length > 1 && (
                          <button
                            onClick={() => setDeleteTarget({ id: claim.id, name: claim.name })}
                            className="p-1.5 rounded-lg hover:bg-negative-bg transition-colors text-muted-foreground hover:text-negative"
                            title="Delete claim"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                        <button
                          onClick={() => handleSwitch(claim.id)}
                          disabled={isSwitching}
                          className={[
                            'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ml-1',
                            isActive
                              ? 'bg-primary/10 text-primary hover:bg-primary/20' :'bg-primary text-primary-foreground hover:bg-amber-700',
                          ].join(' ')}
                        >
                          {isSwitching ? (
                            <Loader2 size={12} className="animate-spin" />
                          ) : isActive ? (
                            <Check size={12} />
                          ) : null}
                          {isActive ? 'Open' : 'Switch'}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Delete confirmation */}
      <ConfirmModal
        open={!!deleteTarget}
        title="Delete Claim"
        description={`Are you sure you want to delete "${deleteTarget?.name}"? All items and data for this claim will be permanently removed.`}
        confirmLabel={deleting ? 'Deleting…' : 'Delete'}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
